import { useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Eye, LogOut } from "lucide-react";
import { ProductSwitch, SubNav, Utilities } from "@/components/ClientNav";
import { ReplyAlerts } from "@/components/outreach/ReplyAlerts";
import { DigestSettings } from "@/components/clients/DigestSettings";
import { exitViewAs, viewingAs } from "@/lib/clientAdmin";
import { themeCss } from "@/lib/clientTheme";
import { BrandLogo } from "@/components/BrandLogo";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useAuth } from "@/context/AuthContext";
import { canSeeBilling } from "@/lib/clients";
import { cn } from "@/lib/utils";

// §78 Slim shell for invited clients: only the modules their workspace includes.
export const ClientLayout = () => {
  const { client, user, logout } = useAuth();
  const loc = useLocation();
  const va = viewingAs();
  const css = themeCss(client?.brand?.accent, client?.brand?.tint);
  useEffect(() => { const el = document.documentElement; if (css) el.setAttribute("data-client-theme", ""); return () => el.removeAttribute("data-client-theme"); }, [css]);
  return (
    <div className="grain min-h-screen bg-[#09090B]" data-testid="client-layout" data-client-theme={css ? "" : undefined}>
      {css && <style data-testid="client-theme-style">{css}</style>}
      {va && <div className="flex flex-wrap items-center justify-center gap-3 bg-[#8224e3] px-4 py-1.5 text-xs text-white" data-testid="client-viewas-banner"><Eye className="h-3.5 w-3.5" />Viewing as {va} (read-only)<button onClick={exitViewAs} className="rounded-full bg-white/20 px-2.5 py-0.5 font-semibold hover:bg-white/30" data-testid="client-viewas-exit">Back to Client Admin</button></div>}
      <header className="sticky top-0 z-20 border-b border-[#27272A] bg-[#09090B]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3 px-4 py-3 md:px-8">
          <span data-our-brand><BrandLogo size="sm" tagline={false} /></span>
          {client?.logo_url && <><span className="text-zinc-600">×</span><img src={client.logo_url} alt={`${client.company} logo`} className="h-7 max-w-[110px] object-contain" data-testid="client-logo" /></>}
          <span className="hidden h-5 w-px bg-[#27272A] sm:block" />
          <span className="hidden text-xs text-zinc-400 md:inline" data-testid="client-company"><span className="font-mono uppercase tracking-[0.2em] text-[#D4AF37] hidden xl:inline">Client portal</span>{client?.company && <span><span className="hidden xl:inline"> · </span>{client.company}</span>}</span>
          <span className="order-last w-full overflow-x-auto [scrollbar-width:none] sm:order-none sm:ml-4 sm:w-auto [&::-webkit-scrollbar]:hidden"><ProductSwitch client={client} /></span>
          <span className="ml-auto flex items-center gap-1"><Utilities client={client} can={canSeeBilling(client)} /></span>
          
          <ReplyAlerts />
          <DigestSettings readOnly={!!va} />
          {!va && <button onClick={logout} title={`Sign out${user?.email ? ` (${user.email})` : ""}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#27272A] bg-[#0f0f12] text-zinc-400 transition-colors hover:border-[#D4AF37]/50 hover:text-[#F3C649]" data-testid="client-logout"><LogOut className="h-3.5 w-3.5" /><span className="sr-only">Sign out</span></button>}
        </div>
        <SubNav client={client} />
      </header>
      <main className="relative z-[2] mx-auto max-w-[1400px] px-4 py-8 md:px-8"><ErrorBoundary key={loc.pathname}><Outlet /></ErrorBoundary></main>
    </div>
  );
};
